import { Select as BaseSelect } from "@base-ui/react/select";
import { useId, type ReactNode } from "react";
import { cx, usePortalContainer } from "../presentation.tsx";
import { Icon } from "../icons.tsx";

export interface SelectOption { value: string; label: string; disabled?: boolean }

export interface SelectProps {
  label: ReactNode;
  options: SelectOption[];
  value?: string | null;
  defaultValue?: string | null;
  onValueChange?: (value: string | null) => void;
  placeholder?: string;
  hint?: ReactNode;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

export function Select({
  label, options, value, defaultValue, onValueChange, placeholder = "Choose…", hint, name, disabled, required, className,
}: SelectProps) {
  const container = usePortalContainer();
  // Named by its label and its current value from the first render: Base UI links the label only
  // after mount, so the server-rendered trigger (and a screen reader reaching it early) had no name.
  const id = useId();
  return (
    <div className={cx("pmu-field", className)}>
      <BaseSelect.Root
        items={options} value={value} defaultValue={defaultValue} name={name} disabled={disabled} required={required}
        onValueChange={onValueChange ? (v) => onValueChange(v) : undefined}
      >
        <BaseSelect.Label className="pmu-field__label"><span id={`${id}-label`}>{label}</span></BaseSelect.Label>
        <BaseSelect.Trigger className="pmu-input pmu-select__trigger" aria-labelledby={`${id}-label ${id}-value`}>
          <BaseSelect.Value id={`${id}-value`} className="pmu-select__value" placeholder={placeholder} />
          <BaseSelect.Icon className="pmu-select__icon"><Icon name="chevron-down" /></BaseSelect.Icon>
        </BaseSelect.Trigger>
        <BaseSelect.Portal container={container}>
          <BaseSelect.Positioner className="pmu-select__positioner" sideOffset={4} alignItemWithTrigger={false}>
            <BaseSelect.Popup className="pmu-popup pmu-select__popup">
              <BaseSelect.List className="pmu-select__list">
                {options.map((o) => (
                  <BaseSelect.Item key={o.value} value={o.value} disabled={o.disabled} className="pmu-select__item">
                    <BaseSelect.ItemIndicator className="pmu-select__check"><Icon name="check" /></BaseSelect.ItemIndicator>
                    <BaseSelect.ItemText>{o.label}</BaseSelect.ItemText>
                  </BaseSelect.Item>
                ))}
              </BaseSelect.List>
            </BaseSelect.Popup>
          </BaseSelect.Positioner>
        </BaseSelect.Portal>
      </BaseSelect.Root>
      {hint && <div className="pmu-field__hint">{hint}</div>}
    </div>
  );
}
